import { useCallback, useEffect, useState } from "react"
import { ApiBalanceParams, checkAggregateSupportedToken, getAggregateQuoteMainToken, getAggregateQuoteTokenAddress, getAssetsByChainBase, getEvmBalance, getEvmPrice } from "services/commonServices"
import { useAccount } from "wagmi"
import { useAppDispatch, useAppSelector } from "./redux/hooks"
import { setAggregator, setBalanceEnough, setBalanceEvm, setFromAmount, setFromTokenUsdt, setGas, setIsMinUsdt, setMiniToAmount, setRouteSummary, setSlippage, setToAmount, setToAmountUsdt } from "./redux/slices/coreInfoSlice"
import { Chain, createPublicClient, formatUnits, http, parseUnits } from "viem"
import { DEF_TOKEN_ADDRESS, SERVICE_FEE_RATE_MOL, SERVICE_FEE_RATE_DEN, DEF_MIN_USDT, getSlippageByToToken } from "utils"
import BigNumber from "bignumber.js";
import useDebounce from "./useDebounce"
import { ChaingeChain, ChaingeToken } from "types/baseType"
import { getDataByEncodeFunctionData } from "utils/viemUtils"
import useIsCrossChain from "./useIsCrossChain"
import { setLoading } from "./redux/slices/appSlice"
import toast from "react-hot-toast"

const useUtils = () => {
    const dispatch = useAppDispatch()
    const { wagmiChains, refreshData, tokens, chains } = useAppSelector((state) => state.app)
    const { fromToken, fromChain, toChain, toToken, balanceEvm, fromAmount, balanceEnough } = useAppSelector((state) => state.coreInfo)
    const { isConnected: isConnectedEvm } = useAppSelector((state) => state.wallet)
    const { address: addressEvm, } = useAccount()
    const { decimals = 6, contractAddress = '' } = fromToken || {}
    const { isCrossChain } = useIsCrossChain()

    // 获取EVM余额
    const getBalanceEvm = useCallback(async () => {
        if(!isConnectedEvm || !addressEvm || !fromToken?.contractAddress || !fromChain?.nickName) return
        const params : ApiBalanceParams = {
            chain: fromChain?.nickName,
            address: addressEvm,
            contractAddress: fromToken.contractAddress
        }
        const balance = await getEvmBalance(params)
        dispatch(setBalanceEvm(balance))
    }, [addressEvm, fromChain?.nickName, dispatch, isConnectedEvm, fromToken?.contractAddress])

    useDebounce(() => {
        getBalanceEvm()
    }, 200, [getBalanceEvm, refreshData])

    // 判断evm 的余额是否足够
    useEffect(() => {
      if(!(isConnectedEvm && fromAmount && balanceEvm)) return
      const amountBigInt = parseUnits(fromAmount, decimals)
      const balanceBigInt = parseUnits(balanceEvm, decimals)
      if(amountBigInt > balanceBigInt) {
        dispatch( setBalanceEnough(false))
      } else {
        dispatch( setBalanceEnough(true))
      }
    }, [balanceEvm, fromAmount, isConnectedEvm, decimals, dispatch])

    // 设置最大值
    const setMaxBalance = useCallback(async () => {
        if(!isConnectedEvm) return
        if(contractAddress === DEF_TOKEN_ADDRESS) {
            const curWagmiChain = wagmiChains.find(item => item.id === +fromChain?.network)
            if(curWagmiChain) {
                const publicClient = createPublicClient({
                    chain: curWagmiChain,
                    transport: http()
                }) as any
                let gasPrice : bigint = await publicClient?.getGasPrice()
                let gasLimit = BigInt(21000);
                if(fromChain?.nickName === 'OP') {
                    const double = BigInt(2)
                    gasLimit = gasLimit * double
                }
                const { decimals = 18 } = fromToken || {}
                const gasAmount = gasPrice*gasLimit
                const balanceEvmBigint = parseUnits(balanceEvm, decimals)
                const maxAmount = balanceEvmBigint - gasAmount
                const maxAmountStr = formatUnits(maxAmount, decimals)
                dispatch(setFromAmount(maxAmountStr))
            }
        } else {
            dispatch(setFromAmount(balanceEvm))
        }
    }, [balanceEvm, contractAddress, dispatch, fromChain?.network, fromChain?.nickName, fromToken, isConnectedEvm, wagmiChains])

    // 获取gasFee
    interface GasFeeParams {
        fromAmount: string
        toChain: ChaingeChain
        toToken: ChaingeToken
        wagmiChains: Chain[]
    }
    const getGasFee = useCallback(async (params: GasFeeParams) => {
        const { fromAmount, toChain, toToken, wagmiChains } = params
       try {
            const curWagmiChain = wagmiChains.find(item => item.id === +toChain?.network)
            if(curWagmiChain) {
                const publicClient = createPublicClient({
                    chain: curWagmiChain,
                    transport: http()
                }) as any
 
                let gasPrice : bigint = await publicClient?.getGasPrice()
                let gasLimit = BigInt(0)
                const amount = parseUnits(fromAmount, toToken.decimals)
                if(toToken.contractAddress === DEF_TOKEN_ADDRESS) {
                    // 本币转账
                    gasLimit = await publicClient.estimateGas({ 
                        account: `0x456325f2ac7067234dd71e01bebe032b0255e039`,
                        to: '0x42a6685ef29886Cbcb595Aa903f00dea0d1787d8',
                        value: amount,
                        gasPrice: gasPrice
                    })
                    if(fromChain?.nickName === 'OP') {
                        const double = BigInt(2)
                        gasLimit = gasLimit * double
                    }
                } else {
                    const data = getDataByEncodeFunctionData(amount, `0x456325f2ac7067234dd71e01bebe032b0255e039`)
                    gasLimit = await publicClient.estimateGas({ 
                        account: `0x456325f2ac7067234dd71e01bebe032b0255e039`,
                        to: '0x42a6685ef29886Cbcb595Aa903f00dea0d1787d8',
                        gasPrice: gasPrice,
                        data: data
                    })
                }
                // 获取toChain 本币 价格
                const toTokenPrice =  await getEvmPrice({chain: toChain.nickName, symbol: curWagmiChain.nativeCurrency.symbol, contractAddress: DEF_TOKEN_ADDRESS})
                const tempGasFee = gasPrice * gasLimit
                const gasAmount = formatUnits(tempGasFee, 18)
                const gasAmountBigNumber = new BigNumber(gasAmount)
                const tokenPriceBigNumber = new BigNumber(toTokenPrice)
                return gasAmountBigNumber.multipliedBy(tokenPriceBigNumber)
            }
       } catch(error) {
            throw error
       }
    }, [fromChain?.nickName])

    // 重制询价参数
    const resetQuoteInfo = useCallback(() => {
        dispatch(setToAmount('0.00'))
        dispatch(setGas(`0.00`))
        dispatch(setAggregator(''))
        dispatch(setRouteSummary(''))
        dispatch(setToAmountUsdt('0.00'))
        dispatch(setSlippage(0))
        dispatch(setMiniToAmount('0.00'))
        dispatch(setIsMinUsdt(false))
    }, [dispatch])

    const getFromTokenUsdt = useCallback(async () => {
        const fromTokenPrice =  await getEvmPrice({chain: fromChain?.nickName, symbol: fromToken?.symbol, contractAddress: fromToken?.contractAddress})
        dispatch(setFromTokenUsdt(fromTokenPrice))
        const min5UsdtBigNumber = new BigNumber(DEF_MIN_USDT)
        const fromTokenPriceBigNumber = new BigNumber(fromTokenPrice)
        const tempFromAmount = new BigNumber(fromAmount)
        const fromAmountUsdt = tempFromAmount.multipliedBy(fromTokenPriceBigNumber)
        if(!fromAmountUsdt.gte(min5UsdtBigNumber)) {
            dispatch(setIsMinUsdt(true))
        } else {
            dispatch(setIsMinUsdt(false))
        }
    }, [dispatch, fromAmount, fromChain?.nickName, fromToken?.contractAddress, fromToken?.symbol])

    // 跨链询价
    // 设置toAmount
    useDebounce(async () => {
        try {
            if(!isCrossChain) return
            dispatch(setLoading(true))
            resetQuoteInfo()
            if(!(fromAmount && +fromAmount > 0 && fromToken && fromChain && toToken && toChain && wagmiChains.length)) {
                return
            }
            const gasFeeBigNumber: BigNumber = await getGasFee({fromAmount, toChain, toToken, wagmiChains}) as BigNumber
            const fromTokenPrice =  await getEvmPrice({chain: fromChain?.nickName, symbol: fromToken.symbol, contractAddress: fromToken.contractAddress})
            dispatch(setFromTokenUsdt(fromTokenPrice))
            // 计算 to Amount
            const tempFromAmount = new BigNumber(fromAmount)
            // 当前币的价值
            const fromTokenPriceBigNumber = new BigNumber(fromTokenPrice)
            const fromAmountUsdt = tempFromAmount.multipliedBy(fromTokenPriceBigNumber)
            // 5U的价值
            const min5UsdtBigNumber = new BigNumber(DEF_MIN_USDT)
            if(!fromAmountUsdt.gte(min5UsdtBigNumber)) {
                dispatch(setIsMinUsdt(true))
            } else {
                dispatch(setIsMinUsdt(false))
            }

            // 分子
            const tempMol = new BigNumber(SERVICE_FEE_RATE_MOL)
            // 分母
            const tempDen = new BigNumber(SERVICE_FEE_RATE_DEN)
    
            // 服务端手续费
            const serviceRate = tempMol.div(tempDen)
            const serviceFee = serviceRate.multipliedBy(tempFromAmount)

            // 减去服务费后的
            const remainAmount = tempFromAmount.minus(serviceFee)
            const remainAmountUsdt = remainAmount.multipliedBy(fromTokenPriceBigNumber)

            if(remainAmountUsdt.lte(gasFeeBigNumber)) {
                // 跨链价值 无法 覆盖 gasFee
                toast.error("Gas cannot be covered.", {
                    duration: 3000,
                    position: 'top-right',
                })
                return
            }
            const gasFeeFromTokenAmount = gasFeeBigNumber.div(fromTokenPriceBigNumber)
            const gasFeeFromTokenAmountFormat = new BigNumber(gasFeeFromTokenAmount.toFixed(fromToken.decimals))
            const gas = serviceFee.plus(gasFeeFromTokenAmountFormat)

            const tempToAmount = remainAmount.minus(gasFeeFromTokenAmountFormat)
            const tempToAmountUsdt= tempToAmount.multipliedBy(fromTokenPriceBigNumber)
            dispatch(setToAmount(tempToAmount.toString()))
            dispatch(setGas(gas.toString()))
            dispatch(setSlippage(0))
            dispatch(setToAmountUsdt(tempToAmountUsdt.toFixed(2)))
        } catch(error) {
            resetQuoteInfo()
        } finally {
            if(isCrossChain) {
                dispatch(setLoading(false))
            }
        }
    }, 500, [fromAmount, fromToken, fromChain, toToken, toChain, wagmiChains, isCrossChain, getGasFee, dispatch, resetQuoteInfo])

    // 交易询价
    const fnCheckAggregateSupportedToken = useCallback(async () => {
        if((fromChain && fromToken && toChain && toToken && fromAmount && +fromAmount > 0)) {
            const params = {
                fromChain,
                toChain,
                fromToken,
                toToken
            }
            const isQuoteMain = await checkAggregateSupportedToken(params)
            return isQuoteMain
        }
    }, [fromChain, fromToken, toChain, toToken, fromAmount])

    const fnGetAggregateQuoteMainToken = useCallback(async (supportChains: string[]) => {
        if(fromAmount && fromToken && toToken && toChain && fromChain && +fromAmount > 0) {
            // const amount = parseUnits(fromAmount, fromToken?.decimals).toString()
            const tempChainIDs = []
            if(supportChains.includes(fromChain?.network)) {
                tempChainIDs.push(fromChain?.network)
            }
            if(supportChains.includes(toChain.network)) {
                tempChainIDs.push(toChain?.network)
            }
            let chainIDs = ''
            if(tempChainIDs.length > 0) {
                chainIDs = tempChainIDs.join(",")
            }
            
            const amount = fromAmount
            const params = {
                fromSymbol: fromToken.symbol,
                toSymbol: toToken.symbol,
                fromAmount: amount,
                chainIDs
            }
            const result = await getAggregateQuoteMainToken(params, fromChain?.network, toChain?.network)
            if(result) {
                const { amountOut, chainId } = result
                const curChain = chains.find(item => item.network === chainId)
                if(curChain) {
                    const chainName = curChain.nickName
                    const curBaseToken = await getAssetsByChainBase(chainName, toToken?.symbol)
                    if(curBaseToken) {
                        result['toAmount'] = formatUnits(amountOut, curBaseToken.decimals)
                        result['slippage'] = getSlippageByToToken(fromToken.symbol, toToken.symbol)
                        return result
                    }
                    return 'error'
                }
                return 'error'
            }
            return 'error'
        } else {
            return '' 
        }
    }, [fromAmount, fromToken, toToken, fromChain, toChain, chains])


    const fnGetAggregateQuoteTokenAddressByFrom = useCallback(async() => {
        if(fromChain && fromToken && toToken && fromAmount && +fromAmount > 0) {
            const fromContractAddress = fromToken.contractAddress
            const fromDecimals = fromToken.decimals
            let toContractAddress = ''
            let toDecimals = -1
            const toTokenAll = tokens.find(item => item.symbol === toToken.symbol)
            if(toTokenAll) {
                const contractAddressList =  toTokenAll.contracts
                const curToTokenAddress =  contractAddressList[fromChain.nickName]
                if(curToTokenAddress) {
                    toContractAddress = curToTokenAddress['address']
                    toDecimals = curToTokenAddress['decimals']
                }
            }
            if(fromContractAddress && toContractAddress && fromDecimals > 0 && toDecimals >0) {
                const amount = parseUnits(fromAmount, fromDecimals)
                // const amount = fromAmount
                const params = {
                    chain: fromChain?.nickName,
                    fromToken: fromContractAddress,
                    toToken: toContractAddress,
                    fromDecimals: fromDecimals,
                    toDecimals: toDecimals,
                    fromAmount: amount.toString()
                }
                const result = await getAggregateQuoteTokenAddress(params, fromChain?.network)
                if(result) {
                    const { amountOut, chainId } = result
                    const curChain = chains.find(item => item.network === chainId)
                    if(curChain) {
                        const chainName = curChain.nickName
                        const curBaseToken = await getAssetsByChainBase(chainName,  toToken?.symbol)
                        if(curBaseToken) {
                            result['toAmount'] = formatUnits(amountOut, curBaseToken.decimals)
                            result['slippage'] = getSlippageByToToken(fromToken.symbol, toToken.symbol)
                            return result
                        }
                        return 'error'
                    }
                    return 'error'
                }
                return 'error'
            }
            return 'error'
        } else {
            return ''
        }
    }, [fromAmount, fromChain, fromToken, toToken, tokens, chains])

    const fnGetAggregateQuoteTokenAddressByTo = useCallback(async() => {
        if(toChain && fromToken && toToken && fromAmount && +fromAmount > 0) {
            const toContractAddress = toToken.contractAddress
            const toDecimals = toToken.decimals
            let fromContractAddress = ''
            let fromDecimals = -1
            const fromTokenAll = tokens.find(item => item.symbol === fromToken.symbol)
            if(fromTokenAll) {
                const contractAddressList =  fromTokenAll.contracts
                const curToTokenAddress =  contractAddressList[toChain.nickName]
                if(curToTokenAddress) {
                    fromContractAddress = curToTokenAddress['address']
                    fromDecimals = curToTokenAddress['decimals']
                }
            }
            if(fromContractAddress && toContractAddress && fromDecimals > 0 && toDecimals >0) {
                const amount = parseUnits(fromAmount, fromDecimals)
                // const amount = fromAmount
                const params = {
                    chain: toChain?.nickName,
                    fromToken: fromContractAddress,
                    toToken: toContractAddress,
                    fromDecimals: fromDecimals,
                    toDecimals: toDecimals,
                    fromAmount: amount.toString()
                }
                const result = await getAggregateQuoteTokenAddress(params, toChain?.network)
                if(result) {
                    const { amountOut, chainId } = result
                    const curChain = chains.find(item => item.network === chainId)
                    if(curChain) {
                        const chainName = curChain.nickName
                        const curBaseToken = await getAssetsByChainBase(chainName,  toToken?.symbol)
                        if(curBaseToken) {
                            result['toAmount'] = formatUnits(amountOut, curBaseToken.decimals)
                            result['slippage'] = getSlippageByToToken(fromToken.symbol, toToken.symbol)
                            return result
                        }
                        return 'error'
                    }
                    return 'error'
                }
                return 'error'
            }
            return 'error'
        } else {
            return ''
        }
    }, [chains, fromAmount, fromToken, toChain, toToken, tokens])

    const setAggregateQuoteVal = useCallback((tempResult: any) => {
        const { aggregator, toAmount, gasUSD, routeSummary="", amountOutUSD, slippage } = tempResult
        dispatch(setToAmount(toAmount))
        dispatch(setGas(`${gasUSD}`))
        dispatch(setAggregator(aggregator))
        dispatch(setRouteSummary(routeSummary))
        dispatch(setToAmountUsdt(amountOutUSD))
        dispatch(setSlippage(slippage))

        const tempMimiToAmount = toAmount - toAmount * (slippage / 100)
        dispatch(setMiniToAmount(tempMimiToAmount.toString()))
    }, [dispatch])


    useDebounce(async () => {
        let errorMsg = ''
        try {
            if(isCrossChain) return
            resetQuoteInfo()
            if(+fromAmount <= 0) return
            await getFromTokenUsdt()
            dispatch(setLoading(true))
            const supportTokenResult = await fnCheckAggregateSupportedToken()
            if(!supportTokenResult) return
            if(supportTokenResult && supportTokenResult.isMainToken) {
                const result = await fnGetAggregateQuoteMainToken(supportTokenResult.supportChains)
                if(result && result !== 'error') {
                    setAggregateQuoteVal(result)
                    return
                }
                if(result === 'error') {
                    errorMsg = 'The transaction pair is not supported.'
                    dispatch(setFromAmount(''))
                }
            } else {
                const resultFrom = await fnGetAggregateQuoteTokenAddressByFrom()
                const resultTo = await fnGetAggregateQuoteTokenAddressByTo()
                if((resultFrom && resultFrom !== 'error') && (resultTo && resultTo !== 'error')) {
                    let tempResult =  {}
                    if(resultFrom.amountOutUSD > resultTo.amountOutUSD) {
                        tempResult = resultFrom
                    } else {
                        tempResult = resultTo
                    }
                    setAggregateQuoteVal(tempResult)
                    return
                } else if(resultFrom && resultFrom !== 'error') {
                    setAggregateQuoteVal(resultFrom)
                    return
                } else if(resultTo && resultTo !== 'error') {
                    setAggregateQuoteVal(resultTo)
                    return
                }

                if(resultFrom === 'error' && resultTo === 'error') {
                    dispatch(setFromAmount(''))
                    errorMsg = 'The transaction pair is not supported.'
                }
            }
        } catch(error) {
            debugger
            errorMsg = 'Service is unavailable, please try again later.'
            dispatch(setFromAmount(''))
        } finally {
            if(!isCrossChain) {
                dispatch(setLoading(false))
            }
            if(errorMsg) {
                toast.error(errorMsg, {
                    duration: 4000,
                    position: 'top-right',
                })
            }
        }
    }, 500, [isCrossChain, fromAmount, fnCheckAggregateSupportedToken, fnGetAggregateQuoteMainToken, fnGetAggregateQuoteTokenAddressByFrom, fnGetAggregateQuoteTokenAddressByTo, resetQuoteInfo, dispatch, getFromTokenUsdt])

    return {balanceEnough, isConnectedEvm, toToken, toChain,  getBalanceEvm, setMaxBalance}
}

export default useUtils